import React, { useCallback, useEffect, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import ContabilidadGeneral from "./ContabilidadGeneral";
import Button from "../../Elements/Button";

const CATEGORIES = [
  { id: "ventas",    label: "Ventas / Dispensario", color: "#84cc16", unit: "€" },
  { id: "depositos", label: "Monedero / Depósitos",  color: "#0ea5e9", unit: "€" },
  { id: "socios",    label: "Cuotas Socios",         color: "#f59e0b", unit: "socios" },
  { id: "pagos",     label: "Gastos y compras",      color: "#8b5cf6", unit: "€" },
];

function today()      { return new Date().toISOString().slice(0, 10); }
function oneYearAgo() {
  const d = new Date(); d.setFullYear(d.getFullYear() - 1);
  return d.toISOString().slice(0, 10);
}

function fmt(n, unit) {
  if (unit === "€") return `${Number(n || 0).toFixed(2)} €`;
  return `${Number(n || 0)} ${unit}`;
}

const inputCls =
  "border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm bg-white " +
  "focus:outline-none focus:ring-2 focus:ring-[var(--ring-selected)]";

function PeriodPanel({ title, data, color, unit }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <div className="flex-1 min-w-0 bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="bg-gray-50 border-b border-gray-200 px-3 py-2">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{title}</span>
      </div>
      <div className="p-3">
        <div className="h-32">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 9, fill: "#9ca3af" }} />
              <YAxis tick={{ fontSize: 9, fill: "#9ca3af" }} />
              <Tooltip
                formatter={(v) => [fmt(v, unit), "Valor"]}
                contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e5e7eb" }}
              />
              <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-2 space-y-0.5 max-h-36 overflow-y-auto">
          {data.map((row, i) => (
            <div key={i} className={`flex justify-between text-xs px-1 py-0.5 rounded ${i % 2 ? "bg-gray-50" : ""}`}>
              <span className="text-gray-500">{row.label}</span>
              <span className="font-medium text-gray-800">{fmt(row.value, unit)}</span>
            </div>
          ))}
        </div>
        <div className="mt-2 pt-2 border-t border-gray-100 flex justify-between text-xs font-semibold">
          <span className="text-gray-500">TOTAL</span>
          <span style={{ color }}>{fmt(total, unit)}</span>
        </div>
      </div>
    </div>
  );
}

export default function BalancePorPeriodo({ token, venueID }) {
  const [activeCategory, setActiveCategory] = useState("ventas");
  // Dentro de "Ventas / Dispensario": gráficas (por defecto) o balance por genética.
  const [ventasView, setVentasView] = useState("graficas"); // 'graficas' | 'genetica'
  const [fromDate, setFromDate] = useState(oneYearAgo);
  const [toDate,   setToDate]   = useState(today);
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");

  const fetchData = useCallback(async () => {
    if (!token || !venueID) return;
    setLoading(true); setError("");
    try {
      const url =
        `/v1/reports/balance-period?venue_id=${venueID}` +
        `&category=${activeCategory}&from_date=${fromDate}&to_date=${toDate}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Error al cargar datos");
      setData(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, venueID, activeCategory, fromDate, toDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const cat = CATEGORIES.find((c) => c.id === activeCategory);
  // Dentro de Ventas/Dispensario: vista "Balance por genética" (tabla danimateu).
  const showGenetica = activeCategory === "ventas" && ventasView === "genetica";
  // Conmutador Gráficas | Balance por genética (se muestra en la fila de las zonas).
  const ventasToggle = (
    <div className="inline-flex gap-1 rounded-lg bg-gray-100 p-1">
      {[["graficas", "Gráficas"], ["genetica", "Balance"]].map(([k, label]) => (
        <button
          key={k}
          type="button"
          onClick={() => setVentasView(k)}
          className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
            ventasView === k ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-3 flex flex-wrap items-center gap-3">
        {/* Category sub-tabs */}
        <div className="wa-reportes-tabs-row flex flex-wrap gap-1">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              onClick={() => setActiveCategory(c.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border ${
                activeCategory === c.id
                  ? "text-white border-transparent shadow-sm"
                  : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
              }`}
              style={activeCategory === c.id ? { backgroundColor: c.color, borderColor: c.color } : {}}
            >
              {c.label}
            </button>
          ))}
        </div>

        {/* Date range (solo en modo gráficas; el balance por genética trae su propio filtro) */}
        <div className={`flex items-center gap-2 ml-auto flex-wrap ${showGenetica ? "hidden" : ""}`}>
          <div className="flex items-center gap-1.5 text-sm">
            <label className="text-gray-500 font-medium whitespace-nowrap">Desde</label>
            <input
              type="date"
              className={inputCls}
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div className="wa-reportes-hasta-group flex items-center gap-1.5 text-sm">
            <label className="text-gray-500 font-medium whitespace-nowrap">hasta</label>
            <input
              type="date"
              className={inputCls}
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
          <Button onClick={fetchData} disabled={loading}>
            {loading ? "…" : "Filtrar"}
          </Button>
        </div>
      </div>

      {/* Modo gráficas en Ventas/Dispensario: el conmutador va en su propia fila */}
      {activeCategory === "ventas" && !showGenetica && ventasToggle}

      {/* Balance por genética (tabla danimateu): el conmutador va en la fila de las zonas */}
      {showGenetica && (
        <ContabilidadGeneral token={token} venueID={venueID} beforeZones={ventasToggle} />
      )}

      {!showGenetica && error && <div className="text-sm text-red-500 bg-red-50 rounded-lg px-3 py-2">{error}</div>}

      {!showGenetica && loading && (
        <div className="grid grid-cols-3 gap-3">
          {[0, 1, 2].map((i) => <div key={i} className="h-64 bg-gray-100 rounded-xl animate-pulse" />)}
        </div>
      )}

      {!showGenetica && !loading && !error && data && (
        <>
          <div className="text-xs text-gray-400 px-1">
            <span className="font-semibold text-gray-600">{cat?.label}</span>
            {" · "}
            {data.from_date} → {data.to_date}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <PeriodPanel title="Días"   data={data.days   || []} color={cat?.color} unit={data.unit} />
            <PeriodPanel title="Semanas" data={data.weeks || []} color={cat?.color} unit={data.unit} />
            <PeriodPanel title="Meses"  data={data.months || []} color={cat?.color} unit={data.unit} />
          </div>
        </>
      )}
    </div>
  );
}
